import m from '@bitstillery/mithril'
import {Icon} from '@bitstillery/common/components/portal'
import {classes, get_route, is_one_of} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {state} from '@bitstillery/mithril'
import {$t, events} from '@bitstillery/common/app'
import {remove_diacritics} from '@bitstillery/common/utils'

import type {Unwatch} from '@bitstillery/mithril'

import {$m, $s} from '@/app'
import {require_login} from '@/components/auth/require_login'
import {apply_category_filter_options, filters} from '@/components/pages/offers/offer_list'
import type {SuggestionType} from '@/models/filter'
import {is_number_list, is_string_list} from '@/lib/filter_selection'

const SUGGESTION_TYPES = ['brand_holder', 'category', 'search', 'tbos'] as const

/**
 * Below this a term matches nearly everything, so the backend drops it from the query; a chip for
 * one would sit in the filter bar looking like it narrowed the result while it did nothing.
 */
const MIN_SEARCH_TERM_LENGTH = 3

/**
 * Split raw search input into separate terms on the comma.
 *
 * The comma is already the separator `filters_sort_to_url` serializes the selection with, so a term
 * containing one cannot survive a page load anyway. Splitting here is what makes a search issued
 * from the shop agree with the same search issued from any other page, which reaches the shop
 * through that url.
 */
export function split_search_terms(input: string): string[] {
    return input
        .split(',')
        .map((term) => term.trim())
        .filter((term) => term.length >= MIN_SEARCH_TERM_LENGTH)
}

/** Append terms to a selection, skipping the ones already in it. */
function add_search_terms(selection: string[], terms: string[]) {
    const added = terms.filter((term, index) => !selection.includes(term) && terms.indexOf(term) === index)
    if (added.length > 0) {
        selection.push(...added)
    }
}

interface SuggestionMatches {
    brand_holder: SuggestionType[]
    category: SuggestionType[]
    cumulative_indexes: number[]
    indexes: number[]
    search: SuggestionType[]
    suggestions: SuggestionType[]
    tbos: SuggestionType[]
}

/**
 * A Search component that provides suggestions from
 * multiple sources. An issued search will always add
 * an extra filter selection, unless its a radio-group
 * type filter.
 */
export class SearchSite extends MithrilComponent {
    data = state(
        {
            collapsed: true,
            placeholder: '',
        },
        'search-site-data',
    )

    matches: SuggestionMatches = {
        brand_holder: [],
        category: [],
        cumulative_indexes: [],
        indexes: [],
        search: [],
        suggestions: [],
        tbos: [],
    }

    brand_holders: SuggestionType[] = []
    /** Pool behind the `search` group: stock names when logged in, every product name otherwise. */
    search_product_names: SuggestionType[] = []
    /** Empty without a login — the TBO group is not shown there. */
    tbo_product_names: SuggestionType[] = []
    search_categories: SuggestionType[] = []
    /** Rounded display counts for non-auth (from public/search-counts). Null when authenticated. */
    search_counts: {
        brand_holder: number
        category: number
    } | null = null
    watchers: Unwatch[] = []
    remove_on_click_outside: (() => void) | null = null
    /**
     * Which side of the login the loaded catalog was fetched for; null while nothing is loaded. The
     * two are cut differently — the public one has no TBO group — so a load only serves its own
     * side, and logging in has to fetch again rather than keep the visitor's suggestions.
     */
    private loaded_catalog_is_public: boolean | null = null
    private search_data_promise: Promise<void> | null = null

    /**
     * The path being navigated *to*. Not `m.route.get()`: `locationchange` fires from the patched
     * `pushState` (see `common/lib/router.ts`), at which point the committed mithril route is still
     * the previous page — a dashboard→/shop click would read `/`, skip the load, and leave the
     * categories bar and category filter empty for the rest of the session. `$s.env.uri` is updated
     * by env's own `locationchange` listener, registered at app init and therefore ahead of ours.
     */
    private is_shop_route(): boolean {
        return ($s.env.uri || m.route.get()).startsWith('/shop')
    }

    private readonly on_locationchange_search = () => {
        if ($s.identity.token && this.is_shop_route()) {
            this.ensure_search_data_loaded()
        }
    }

    private ensure_search_data_loaded(opts?: {from_user_interaction?: boolean}): void {
        const use_public = !$s.identity.token
        if (globalThis.__SSR_MODE__ || this.loaded_catalog_is_public === use_public) {
            return
        }
        if (this.search_data_promise) {
            return
        }

        if (!opts?.from_user_interaction && !use_public && !this.is_shop_route()) {
            return
        }
        this.search_data_promise = this.load_search_data(use_public)
            .then(() => {
                this.loaded_catalog_is_public = use_public
            })
            .finally(() => {
                this.search_data_promise = null
            })
    }

    override oninit() {
        events.on('search.match-suggestions', this.match_suggestions.bind(this))
        events.on('identity.login', this.on_locationchange_search)

        if (this.is_shop_route()) {
            this.ensure_search_data_loaded()
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('locationchange', this.on_locationchange_search)
        }
    }

    private async load_search_data(use_public: boolean) {
        const [brand_holders, search_categories, search_product_names, tbo_product_names, search_counts] = await Promise.all([
            $m.filter.get_brand_holders(use_public),
            $m.filter.get_search_categories(use_public),
            // A visitor without a login searches one undifferentiated list of product names; the
            // stock/TBO split is only meaningful — and only shown — once they are a relation.
            $m.filter.get_product_names(use_public ? null : 'stock', use_public),
            use_public ? Promise.resolve([]) : $m.filter.get_product_names('tbo'),
            use_public ? $m.filter.get_search_counts() : Promise.resolve(null),
        ])
        this.brand_holders = brand_holders
        this.search_categories = search_categories
        this.search_product_names = search_product_names
        this.tbo_product_names = tbo_product_names
        this.search_counts = search_counts
        // `$s.categories.menu` has only just landed; the collection's statistics call may already have
        // built the category filter options against an empty menu, so rebuild them now.
        apply_category_filter_options()
        m.redraw()
    }

    override oncreate(vn: m.VnodeDOM) {
        // Not `{once: true}`: the first load may have been the public catalog, and the visitor who
        // then logs in reopens this same search box. `ensure_search_data_loaded` is the guard.
        const kick_load = () => this.ensure_search_data_loaded({from_user_interaction: true})
        vn.dom.addEventListener('click', kick_load)
        vn.dom.addEventListener('focusin', kick_load)

        const outside_click_listener = (event: MouseEvent) => {
            if (!(event.target instanceof Node && vn.dom.contains(event.target)) && !this.data.collapsed) {
                this.data.collapsed = true
            }
        }

        document.addEventListener('click', outside_click_listener)
        this.remove_on_click_outside = () => {
            document.removeEventListener('click', outside_click_listener)
        }
    }

    override onremove() {
        this.remove_on_click_outside?.()
        events.off('identity.login', this.on_locationchange_search)
        if (typeof window !== 'undefined') {
            window.removeEventListener('locationchange', this.on_locationchange_search)
        }
        this.watchers.map((unwatch) => unwatch())
    }

    apply_filter(use_suggestion = false) {
        if ($s.suggestions.selected.index === null && use_suggestion) {
            return
        }
        let search_data: SuggestionType

        if (use_suggestion && $s.suggestions.selected.index !== null) {
            // The selected index can point past the suggestions once they shrink.
            const suggestion = this.matches.suggestions[$s.suggestions.selected.index]
            if (!suggestion) {
                return
            }

            search_data = suggestion
        } else {
            if (!filters.search.input) {
                return
            }
            search_data = {
                children: null,
                count: null,
                name: filters.search.input,
                parent: null,
                type: 'search',
                value: filters.search.input,
            }
        }

        if (search_data.type === 'category') {
            let artkeys = [search_data.value]
            // Also select the children if it is a parent
            if (search_data.children !== null) {
                artkeys = artkeys.concat(search_data.children.map((child) => child.value))
            }

            const selection = filters.category.selection
            if (is_number_list(selection)) {
                artkeys = artkeys.filter((artkey) => !selection.includes(artkey))
                if (artkeys.length > 0) {
                    selection.push(...artkeys)
                }
            }
        } else if (search_data.type === 'search') {
            const selection = filters.search.selection
            if (Array.isArray(selection) && is_string_list(selection)) {
                // A suggestion is one product name and goes in verbatim; typed input may be a list.
                add_search_terms(selection, use_suggestion ? [search_data.value] : split_search_terms(search_data.value))
            }
            events.emit('search', filters.search.selection)
        } else {
            filters.brand_holder.selection = search_data.value
        }

        // Default action after a search is to clear the input
        // And close the search widget.
        filters.search.input = ''
        this.data.collapsed = true

        // Navigate to the inventory page, with the filter state applied
        // In the url. The offer module will reconstruct the filter selection
        // From the url.
        if (!m.route.get().startsWith('/shop')) {
            // Not yet in the offer module; route to it using search parameters.
            const navigate = (target: string) => require_login(target)
            if (search_data.type === 'category') {
                navigate(get_route('/shop', {category: filters.category.selection}))
            } else if (search_data.type === 'brand_holder') {
                navigate(
                    get_route('/shop', {
                        brand_holder: encodeURIComponent(String(filters.brand_holder.selection)),
                    }),
                )
            } else {
                const terms = filters.search.selection
                const additional_params: {availability?: string; search: string} = {
                    search: encodeURIComponent(Array.isArray(terms) ? terms.join(',') : terms),
                }
                if (search_data.offer_item_type === 'stock') {
                    additional_params['availability'] = 'stock'
                }
                if (search_data.offer_item_type === 'tbo') {
                    additional_params['availability'] = 'tbo'
                }
                navigate(
                    get_route('/shop', {
                        ...additional_params,
                    }),
                )
            }
        } else {
            // Already at the offer module. Just trigger the search event
            // And let the search listener there take over.
            const tbo_availability_filter_enabled = filters.availability.selection.findIndex((it) => it === 'tbo') >= 0
            const stock_availability_filter_enabled = filters.availability.selection.findIndex((it) => it === 'stock') >= 0
            const is_requesting_tbo_page = search_data.offer_item_type === 'tbo'
            const is_requesting_stock_page = search_data.offer_item_type === 'stock'

            // Update the availability filter as requested in the search request.
            const availability = filters.availability.selection
            if (is_string_list(availability)) {
                if (is_requesting_stock_page && !stock_availability_filter_enabled) {
                    availability.push('stock')
                }
                if (is_requesting_tbo_page && !tbo_availability_filter_enabled) {
                    availability.push('tbo')
                }
            }
            events.emit('search', filters.search.selection)
        }
    }

    context_tip() {
        if (this.data.collapsed) {
            return $t('search.placeholder.general')
        }

        if (filters.search.input === '') {
            return $t('search.placeholder.raw')
        }
        return $t('search.placeholder.raw_term', {term: filters.search.input})
    }

    format_suggestion(name: string, count: number | null) {
        if (count !== null) {
            return (
                <span className='name'>
                    {name} ({count})
                </span>
            )
        }
        return <span className='name'>{name}</span>
    }

    match_suggestions() {
        const search_term = filters.search.input.toLowerCase()
        if (!search_term) {
            this.matches.brand_holder.splice(0, this.matches.brand_holder.length, ...this.brand_holders)
            this.matches.search.splice(0, this.matches.search.length, ...this.search_product_names)
            this.matches.tbos.splice(0, this.matches.tbos.length, ...this.tbo_product_names)
            this.matches.category.splice(0, this.matches.category.length, ...this.search_categories)
            this.update_matches()
            return
        }

        this.matches.brand_holder.splice(
            0,
            this.matches.brand_holder.length,
            ...this.brand_holders.filter((suggestion: SuggestionType) =>
                remove_diacritics(suggestion.name).includes(remove_diacritics(search_term)),
            ),
        )

        this.matches.category.splice(
            0,
            this.matches.category.length,
            ...this.search_categories.filter((suggestion: SuggestionType) => suggestion.name.includes(search_term)),
        )

        const _search = search_term.split(' ')
        this.matches.search = this.search_product_names.filter((suggestion: SuggestionType) =>
            _search.every((term) => suggestion.name.includes(term)),
        )
        this.matches.tbos = this.tbo_product_names.filter((suggestion: SuggestionType) =>
            _search.every((term) => suggestion.name.includes(term)),
        )

        this.update_matches()
    }

    make_safe_index(new_index: number | null): number | null {
        if (new_index === null) {
            return null
        }
        const max = this.matches.suggestions.length
        if (new_index < 0 || new_index >= max) {
            return null
        }
        return new_index
    }

    navigate(keyboard_event: KeyboardEvent | 'reset') {
        const index_is_null = $s.suggestions.selected.index === null
        const index = $s.suggestions.selected.index || 0
        const pressed_key = typeof keyboard_event !== 'string' ? keyboard_event.key : ''

        if (pressed_key === 'Escape') {
            // Reset search
            this.data.collapsed = true
            $s.suggestions.selected.index = null
            filters.search.input = ''
            this.apply_filter(false)
        } else if (
            index_is_null &&
            ['ArrowDown', 'PageDown', 'PageUp', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].indexOf(pressed_key) >= 0
        ) {
            if (this.data.collapsed) {
                this.data.collapsed = false
            }
            $s.suggestions.selected.index = this.make_safe_index(0)
        } else if (pressed_key === 'ArrowDown') {
            if (this.data.collapsed) {
                this.data.collapsed = false
            }
            $s.suggestions.selected.index = this.make_safe_index(index + 1)
        } else if (pressed_key === 'PageDown') {
            $s.suggestions.selected.index = this.make_safe_index(index + 10)
        } else if (pressed_key === 'ArrowUp') {
            $s.suggestions.selected.index = this.make_safe_index(index - 1)
        } else if (pressed_key === 'PageUp') {
            $s.suggestions.selected.index = this.make_safe_index(index - 10)
        } else if (pressed_key === 'ArrowRight') {
            // Each column ends where the next one starts; the first column starts at 0.
            const column_ends = this.matches.cumulative_indexes
            const current_column = column_ends.filter((it) => it <= index).length
            const current_start = column_ends[current_column - 1] ?? 0
            const next_start = column_ends[current_column]
            const next_end = column_ends[current_column + 1]
            if (next_start === undefined || next_end === undefined) {
                return true
            }

            $s.suggestions.selected.index = Math.min(next_start + index - current_start, next_end - 1)
        } else if (pressed_key === 'ArrowLeft') {
            const column_ends = this.matches.cumulative_indexes
            const current_column = column_ends.filter((it) => it <= index).length
            const current_start = column_ends[current_column - 1]
            if (current_start === undefined) {
                return true
            }
            const previous_start = column_ends[current_column - 2] ?? 0

            $s.suggestions.selected.index = Math.min(previous_start + index - current_start, current_start - 1)
        } else {
            // Unhandled key pressed or reset
            if ($s.suggestions.selected.index !== null) {
                const search_input: HTMLInputElement | null = document.querySelector('#search-input')
                if (search_input) {
                    search_input.selectionEnd = 10_000
                    search_input.selectionStart = 10_000
                }
            }
            $s.suggestions.selected.index = null // Reset
            return false
        }

        const selected_index = $s.suggestions.selected.index
        const active_suggestion =
            selected_index === null ? null : document.querySelector<HTMLElement>(`#suggestion-${selected_index}`)
        if (!active_suggestion) {
            return true
        }

        const container_height = active_suggestion.parentElement?.clientHeight
        const item_height = active_suggestion.clientHeight
        let scroll_pos = active_suggestion.offsetTop

        if (container_height) {
            if (scroll_pos < container_height) {
                scroll_pos = 0
            } else {
                scroll_pos -= container_height - item_height
            }
        }

        active_suggestion.parentElement?.scrollTo({
            // Smooth scrolling while holding up
            behavior: 'smooth',
            left: 0,
            top: scroll_pos,
        })
        return true
    }

    oninput(e: Event & {currentTarget: HTMLInputElement}) {
        filters.search.input = e.currentTarget.value
        // After adding a search term, the search widget is closed.
        // However, the input is still focussed. Re-open the widget
        // When the user continues typing a next search term.
        if (this.data.collapsed) {
            this.data.collapsed = false
        }

        this.match_suggestions()
    }

    onkeydown(e: KeyboardEvent) {
        // The e.repeat is only true in Chrome; smooth scrolling in Chrome
        // Doesn't work well when a lot of keydown events cause scrollTo
        // Events.Instead, users must use pagedown and pageup keys for
        // Faster scrolling there. Firefox skips this check.
        if (e.repeat) {
            return
        }

        if (e.key === 'Enter') {
            // Enter while the widget is hidden results in opening the widget.
            if (this.data.collapsed) {
                this.data.collapsed = false
            } else if ($s.suggestions.selected.index !== null) {
                this.apply_filter(true)
            } else {
                this.apply_filter(false)
            }
        } else {
            if (this.navigate(e)) {
                /* If dealt with the navigation, swallow the event. */
                e.stopPropagation()
                e.preventDefault()
            }
        }
    }

    /**
     * This adds a feature to the default copy-paste. Multiple tabs or
     * newlines in the text are splitted to multiple search terms. This
     * makes it easier to search multiple items from an excel sheet.
     * @param e - The copy-paste event
     */
    onpaste(e: ClipboardEvent) {
        e.preventDefault()
        const data = e.clipboardData?.getData('text')
        if (!data) {
            return
        }

        const cleaned_data: string[] = data
            .split(/[\n\t]+/)
            .map((i: string) => i.trim())
            .filter((i: string) => i)
        const [first_term, ...more_terms] = cleaned_data
        if (first_term !== undefined && more_terms.length === 0) {
            // A single cell may still hold a comma-separated list; leave it to the input, so the
            // user sees what will be searched and can still edit it before hitting enter.
            filters.search.input = first_term
        } else {
            const current_selection = filters.search.selection
            if (Array.isArray(current_selection) && is_string_list(current_selection)) {
                add_search_terms(current_selection, cleaned_data.flatMap(split_search_terms))
            }
        }
        this.match_suggestions()
    }

    suggestion_delegate(e: MouseEvent, apply_filter = true) {
        const id = e.target instanceof Element ? e.target.getAttribute('id') : null
        if (!id) {
            return
        }
        $s.suggestions.selected.index = Number.parseInt(id.replace('suggestion-', ''))
        if (apply_filter) {
            this.apply_filter(true)
        }
    }

    /** The suggestion groups in the order the state holds them; its proxy lists only data keys. */
    suggestion_type_order() {
        return Object.keys($s.suggestions.types).filter((key) => is_one_of(SUGGESTION_TYPES, key))
    }

    update_matches() {
        const type_order = this.suggestion_type_order()
        this.matches.suggestions = type_order.flatMap((key) => this.matches[key])
        this.matches.indexes = type_order.map((key) => this.matches[key].length)
        let cum = 0
        this.matches.cumulative_indexes = this.matches.indexes.map((len) => {
            cum += len
            return cum
        })
    }

    view() {
        const suggestion_types = this.suggestion_type_order().map((key) => [key, $s.suggestions.types[key]] as const)
        return (
            <div
                className={classes('c-search-site', {
                    active: !this.data.collapsed,
                })}
            >
                <div className='search-box'>
                    <input
                        autocomplete='off'
                        name='search'
                        id={'search-input'}
                        type='text'
                        value={filters.search.input}
                        onfocus={() => {
                            this.match_suggestions()
                            this.data.collapsed = false
                            this.navigate('reset')
                        }}
                        oninput={(e) => this.oninput(e)}
                        onkeydown={(e: KeyboardEvent) => {
                            this.onkeydown(e)
                        }}
                        onpaste={(e: ClipboardEvent) => this.onpaste(e)}
                        placeholder={(() => this.context_tip())()}
                    />

                    <Icon
                        className='btn-search'
                        disabled={filters.search.input.length === 0}
                        name='search'
                        onclick={() => {
                            // Search mode depends on ctrl-key being pressed
                            this.apply_filter(false)
                        }}
                        tip={() => this.context_tip()}
                    />

                    <Icon
                        className='btn-clear'
                        disabled={filters.search.input.length === 0}
                        onclick={() => {
                            filters.search.input = ''
                            this.match_suggestions()
                        }}
                        name='backspace'
                        tip={'Remove Search Term'}
                    />
                </div>
                <div class='suggestions' id={'#suggestions'}>
                    {(() => {
                        let combined_index = 0
                        return suggestion_types.map(([suggestionTypeName, suggestionType]) => {
                            const matches = this.matches[suggestionTypeName]
                            if (matches.length === 0) {
                                return null
                            }
                            // The `search` group holds stock names only once there is a login; for a
                            // visitor it is every product name, so "Stock" would be a lie — and it is
                            // headed without a count, which would read as how much we have on offer.
                            const is_public_products = !$s.identity.token && suggestionTypeName === 'search'
                            const rounded_counts = this.search_counts
                            const display_count =
                                !filters.search.input &&
                                rounded_counts &&
                                (suggestionTypeName === 'brand_holder' || suggestionTypeName === 'category')
                                    ? rounded_counts[suggestionTypeName]
                                    : matches.length
                            return (
                                <div className='suggestion-type'>
                                    <div className='search-header'>
                                        {is_public_products
                                            ? $t('filters.types.products')
                                            : `${$t(suggestionType.name)} (${display_count})`}
                                    </div>
                                    <div
                                        className='suggestion-matches'
                                        onclick={(e) => this.suggestion_delegate(e, true)}
                                        onmouseover={(e) => this.suggestion_delegate(e, false)}
                                    >
                                        {matches.map((suggestion: SuggestionType) => {
                                            const element = (
                                                <button
                                                    className={classes('suggestion', {
                                                        active: combined_index === $s.suggestions.selected.index,
                                                        selected: combined_index === $s.suggestions.selected.index,
                                                    })}
                                                    id={`suggestion-${combined_index}`}
                                                >
                                                    {(() => {
                                                        let elements: m.Vnode[] = []
                                                        if (suggestion.parent) {
                                                            elements = [
                                                                <span className='parent children'>{suggestion.parent.name}</span>,
                                                                <Icon className='icon-d' name='chevronRight' />,
                                                            ]
                                                        }
                                                        elements.push(this.format_suggestion(suggestion.name, suggestion.count))
                                                        return elements
                                                    })()}
                                                </button>
                                            )
                                            combined_index += 1

                                            return element
                                        })}
                                    </div>
                                </div>
                            )
                        })
                    })()}
                </div>
            </div>
        )
    }
}
